import PropTypes from 'prop-types';
import { Box } from '@dextech-ai/dex-design-system';

// Only diff rows carry a marker/variant (a11y §5.1). Unchanged rows (rendered in
// "All fields" mode) get a neutral, content-less gutter that preserves alignment.
const GUTTER = {
	added: { glyph: '+', variant: 'a' },
	modified: { glyph: '~', variant: 'm' },
	removed: { glyph: '−', variant: 'r' },
};

function FieldName({ displayPath }) {
	const path = displayPath || '';
	const lastDot = path.lastIndexOf('.');
	if (lastDot === -1) {
		return <span className="dsv-compare__field-name">{path}</span>;
	}
	const prefix = path.slice(0, lastDot + 1);
	const leaf = path.slice(lastDot + 1);
	return (
		<span className="dsv-compare__field-name">
			<span className="dsv-compare__path-prefix">{prefix}</span>{leaf}
		</span>
	);
}

FieldName.propTypes = { displayPath: PropTypes.string };

function Gutter({ status }) {
	const marker = GUTTER[status];
	if (!marker) return <span className="dsv-compare__gutter" />;
	return <span className={`dsv-compare__gutter dsv-compare__gutter--${marker.variant}`}>{marker.glyph}</span>;
}

Gutter.propTypes = { status: PropTypes.string };

function EmptyCell() {
	return (
		<div className="dsv-compare__col dsv-compare__col--empty">
			<span className="dsv-compare__gap">—</span>
		</div>
	);
}

function PopulatedCell({ status, side, depth, changes, variant }) {
	const indent = { paddingLeft: depth * 16 };
	const isModified = status === 'modified';
	const valueClass = variant === 'before' ? 'dsv-compare__value--before' : 'dsv-compare__value--after';
	return (
		<div className="dsv-compare__col">
			<Gutter status={status} />
			<span className="dsv-compare__field" style={indent}>
				<FieldName displayPath={side.displayPath} />
				{!isModified && (
					<span className="dsv-compare__field-type">{side.dataType}</span>
				)}
				{!isModified && side.mandatory && (
					<span className="dsv-compare__mand">M</span>
				)}
				{isModified && changes.map((c) => (
					<span key={c.property} className="dsv-compare__change">
						<span className="dsv-compare__change-key">{c.property}</span>
						<span className={valueClass}>{variant === 'before' ? c.before : c.after}</span>
					</span>
				))}
			</span>
		</div>
	);
}

PopulatedCell.propTypes = {
	status: PropTypes.string,
	side: PropTypes.object.isRequired,
	depth: PropTypes.number,
	changes: PropTypes.array,
	variant: PropTypes.oneOf(['before', 'after']),
};

PopulatedCell.defaultProps = { depth: 0, changes: [] };

function Row({ row }) {
	const { status, left, right, depth, changes } = row;
	// Status modifier only for diff rows; unchanged rows keep the base class only.
	const rowClass = GUTTER[status]
		? `dsv-compare__row dsv-compare__row--${status}`
		: 'dsv-compare__row';
	return (
		<div
			className={rowClass}
			data-testid={`dsv-compare-row-${status}-${row.displayPath}`}
		>
			{left
				? <PopulatedCell status={status} side={left} depth={depth} changes={changes} variant="before" />
				: <EmptyCell />}
			{right
				? <PopulatedCell status={status} side={right} depth={depth} changes={changes} variant="after" />
				: <EmptyCell />}
		</div>
	);
}

Row.propTypes = { row: PropTypes.object };

function FieldDiffColumns({ rows }) {
	if (!rows || rows.length === 0) {
		return <div className="dsv-compare__empty">No field changes.</div>;
	}
	return (
		<Box display="flex" direction="column">
			{rows.map((row) => <Row key={`${row.status}-${row.displayPath}`} row={row} />)}
		</Box>
	);
}

FieldDiffColumns.propTypes = { rows: PropTypes.array };

export default FieldDiffColumns;
